import { doStoreRedirectSigninUrl } from "./invitations";

const gapi = window.gapi;

// Message returned by the API when the user doesn't exist
export const REGISTRATION_REQUIRED_MESSAGE = "Registration required";
// Message returned by the API when the token has expired
export const CREDENTIALS_REQUIRED_MESSAGE = "Valid user credentials are required.";

// Action types
export const GAPI_CALL = "GAPI_CALL";
export const GAPI_CALL_ERROR = "GAPI_CALL_ERROR";
export const GAPI_INIT = "GAPI_INIT";
export const GAPI_READY = "GAPI_READY";
export const GAPI_ERROR = "GAPI_ERROR";
export const GAPI_SIGNIN = "GAPI_SIGNIN";
export const GAPI_SIGNOUT = "GAPI_SIGNOUT";
export const GAPI_SIGNEDIN = "GAPI_SIGNEDIN";
export const GAPI_SIGNEDOUT = "GAPI_SIGNEDOUT";

/**
 * gapi client api call action creator
 * @param {Function} request Callback that receives gapi.client as parameter
 */
export const gapiCall = request => ({
  type: GAPI_CALL,
  request
});

/**
 * gapi client api error action creator
 * @param {Error} error The error returned by the API
 * @param {Function} request The request that generated the error
 */
export const gapiCallError = (error, request) => ({
  type: GAPI_CALL_ERROR,
  request,
  error
});

/**
 * gapi middleware, responsible for converting gapiCall actions to 
 * corresponding request promises, and trigger gapiErrors
 * @param {*} store 
 */
export const gapiMiddleware = store => next => action => {
  if (action.type === GAPI_CALL) {
    const { request } = action;
    return new Promise(
      (resolve, reject) => {
        try {
          return request(gapi.client)
            .then(res => resolve(res.result))
            .catch(error => {
              if (error.result && error.result.error) {
                const gapiError = error.result.error;
                next(gapiCallError(gapiError, request))
                reject(gapiError);
              } else {
                next(gapiCallError(error, request))
                reject(error);
              }
            })
        } catch (e) {
          reject(e);
        }
      }
    );
  }
  return next(action);
}

/**
 * gapi init action creator
 * @param {*} config gapi config
 */
const gapiInit = config => ({
  type: GAPI_INIT,
  config
})

/**
 * gapi ready action creator
 * triggered when client and auth2 are ready
 * @param {*} gapi gapi
 */
const gapiReady = gapi => ({
  type: GAPI_READY,
  gapi
})

/**
 * gapi error action creator
 * triggered when an error occurs initializing gapi
 * @param {Error} error 
 */
const gapiError = error => ({
  type: GAPI_ERROR,
  error
})

/** 
 * gapi signin action creator
 * triggered before google signin is called
 */
const gapiSignin = () => ({
  type: GAPI_SIGNIN
})

/** 
 * gapi signout action creator
 * triggered before google signout is called
 */
const gapiSignout = () => ({
  type: GAPI_SIGNOUT
})

/**
 * gapi signed in action creator
 * triggered when the user has signed in with google
 * @param {*} googleUser The google user data (id, email, firstName, lastName, imageUrl...)
 */
const gapiSignedin = googleUser => ({
  type: GAPI_SIGNEDIN,
  googleUser
})

/** 
 * gapi signed out action creator
 * triggered when the user has signed out
*/
const gapiSignedout = () => ({
  type: GAPI_SIGNEDOUT
})

/**
 * sign in action
 * initiates google signin redirect
 * 
 */
export const signIn = () => dispatch => {
  dispatch(gapiSignin());

  const redirect_uri = document.location.protocol + '//' + document.location.host + '/signin-redirect';
  const auth = gapi.auth2.getAuthInstance();

  const signinOptions = {
    ux_mode: 'redirect',
    redirect_uri: redirect_uri,
    prompt: 'select_account',
  };
  dispatch(doStoreRedirectSigninUrl(document.location.href.substr(document.location.origin.length)));
  auth.signIn(signinOptions);
}

/** 
 * sign out action
 * initiates google disconnection
 */
export const signOut = () => dispatch => {
  dispatch(gapiSignout());
  const auth = gapi.auth2.getAuthInstance();
  // FIXME ?
  auth.disconnect();
}

/** 
 * refreshes access token
 * FIXME: currently unused
 */
export const reloadAuthResponse = () => dispatch => {
  const auth = gapi.auth2.getAuthInstance();
  const user = auth.currentUser.get();
  return user.reloadAuthResponse();
}

/**
 * initializes gapi loading and redirects to sign in if necessary
 * returns a promise that resolves when client is ready and user signed in
 * @param {*} config Gapi config
 */
export const initGapi = config => dispatch => new Promise((resolve, reject) => {

  // error callback, rejects promise
  const onError = error => {
    dispatch(gapiError(error));
    reject(error);
  }

  // signed in promise, called when signin status changed, 
  // and/or at start, if user is already signed in 
  const onSignedInChange = isSignedIn => {
    if (isSignedIn) {
      const currentUser = gapi.auth2.getAuthInstance().currentUser.get();
      const basicProfile = currentUser.getBasicProfile();

      const googleUser = {
        id: basicProfile.getId(),
        email: basicProfile.getEmail(),
        firstName: basicProfile.getGivenName(),
        lastName: basicProfile.getFamilyName(),
        imageUrl: basicProfile.getImageUrl()
      };

      dispatch(gapiSignedin(googleUser));
    } else {
      dispatch(gapiSignedout());
      // sign back in when user has been signed out
      dispatch(signIn());
    }
  }

  // callback when auth2 has loaded,
  // starts client initialization
  const onAuth2 = () => {
    // init gapi.client
    return gapi.client.init(config)
      .then(() => {
        dispatch(gapiReady(gapi));
        const auth = gapi.auth2.getAuthInstance();
        // call sign in callback if already signed in
        onSignedInChange(auth.isSignedIn.get());
        auth.isSignedIn.listen(onSignedInChange);
        resolve();
      })
      .catch(onError);
  }

  // dispatch starting action
  dispatch(gapiInit(config));

  // load client and auth2 libs
  gapi.load('client:auth2', {
    timeout: 15000,
    callback: onAuth2,
    onerror: onError,
    ontimeout: onError
  });

});




// WEBPACK FOOTER //
// ./src/actions/gapi.js